import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { authAPI } from '../services/api';
import { expandTrekWritePermissions } from '../utils/trekPermissions.js';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const isAdminPanelUser = (userData) => {
  if (!userData) return false;
  if (userData.isAdminPanelUser !== undefined) return !!userData.isAdminPanelUser;
  return userData.role && userData.role !== 'user';
};

/** Match backend rbac: super admin may be stored as "Super Admin", "super-admin", etc. */
const adminRoleKey = (role) => {
  let k = String(role || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  if (k === 'superadmin') k = 'super_admin';
  return k;
};

const isSuperAdminRole = (userData) => adminRoleKey(userData?.role) === 'super_admin';

export const AuthProvider = ({ children }) => {
  const readCachedAdminUser = () => {
    try {
      const raw = localStorage.getItem('admin_user');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      /* Ignore stale cache from before /auth/me sent effectivePermissions — avoids blank admin until refetch. */
      if (isAdminPanelUser(parsed) && !isSuperAdminRole(parsed) && !Array.isArray(parsed.effectivePermissions)) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  };

  const [user, setUser] = useState(readCachedAdminUser);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('admin_token'));

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const fetchUser = async (attempt = 0) => {
    const maxAttempts = 3;
    try {
      const response = await authAPI.getMe();
      const userData = response.data.data?.user || response.data.user;

      if (!userData) {
        logout();
        return;
      }

      if (!isAdminPanelUser(userData)) {
        logout();
        return;
      }

      setUser(userData);
      localStorage.setItem('admin_user', JSON.stringify(userData));
    } catch (error) {
      const status = error.response?.status;
      const isNetworkBlip = !error.response || error.code === 'ECONNABORTED';
      const isTransientServer = status === 503 || status === 502 || status === 504;

      if (status === 401) {
        logout();
        return;
      }

      if ((isNetworkBlip || isTransientServer) && attempt < maxAttempts - 1) {
        await sleep(500 * (attempt + 1));
        return fetchUser(attempt + 1);
      }

      console.error('Failed to fetch user:', error);
      // Keep cached user during temporary 5xx/network outages to avoid forced logout UX.
      const cachedUser = readCachedAdminUser();
      if (cachedUser && isAdminPanelUser(cachedUser)) {
        setUser(cachedUser);
      }
      // Do not clear token on 5xx / transient network — avoids “random” logout when API hiccups.
      // User may need to refresh; if the token is actually invalid the next API call will 401 anyway.
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);

      if (!response.data.success) {
        return {
          success: false,
          message: response.data.message || 'Login failed'
        };
      }

      const { user: u, token: t } = response.data.data || response.data;

      if (u && !isAdminPanelUser(u)) {
        return {
          success: false,
          message: 'Access denied. Admin privileges required.'
        };
      }

      if (!u || !t) {
        return {
          success: false,
          message: 'Invalid response from server'
        };
      }

      setUser(u);
      setToken(t);
      localStorage.setItem('admin_token', t);
      localStorage.setItem('admin_user', JSON.stringify(u));
      localStorage.setItem('admin_last_activity', String(Date.now()));
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Login failed'
      };
    }
  };

  const sendOtp = async (phone) => {
    try {
      const response = await authAPI.sendOtp(phone);
      if (!response.data.success) {
        return { success: false, message: response.data.message || 'Failed to send OTP' };
      }
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to send OTP'
      };
    }
  };

  const loginWithOtp = async (phone, otp) => {
    try {
      const response = await authAPI.verifyOtp(phone, otp);
      if (!response.data.success) {
        return {
          success: false,
          message: response.data.message || 'Verification failed'
        };
      }
      if (response.data.needsRegister) {
        return {
          success: false,
          message: 'This phone is not registered as a user yet. Create the user first from the main site.'
        };
      }
      const data = response.data.data || response.data;
      const { user: u, token: t } = data;
      if (!u || !t) {
        return { success: false, message: 'Invalid response from server' };
      }
      if (!isAdminPanelUser(u)) {
        return {
          success: false,
          message: 'Access denied. Admin privileges required.'
        };
      }
      setUser(u);
      setToken(t);
      localStorage.setItem('admin_token', t);
      localStorage.setItem('admin_user', JSON.stringify(u));
      localStorage.setItem('admin_last_activity', String(Date.now()));
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Verification failed'
      };
    }
  };

  const refreshUser = async () => {
    try {
      const response = await authAPI.getMe();
      const userData = response.data.data?.user || response.data.user;
      if (userData && isAdminPanelUser(userData)) {
        setUser(userData);
        localStorage.setItem('admin_user', JSON.stringify(userData));
      }
    } catch {
      // silently ignore
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    localStorage.removeItem('admin_last_activity');
  };

  // Auto-logout after 2 hours of inactivity while logged in.
  useEffect(() => {
    if (!token) return undefined;

    const IDLE_MS = 2 * 60 * 60 * 1000;
    const STORAGE_KEY = 'admin_last_activity';
    const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    const readLast = () => {
      const raw = Number(localStorage.getItem(STORAGE_KEY) || 0);
      return Number.isFinite(raw) && raw > 0 ? raw : Date.now();
    };

    const bump = () => {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    };

    if (!localStorage.getItem(STORAGE_KEY)) bump();

    let throttleUntil = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now < throttleUntil) return;
      throttleUntil = now + 5000;
      bump();
    };

    const timer = window.setInterval(() => {
      const last = readLast();
      if (Date.now() - last >= IDLE_MS) {
        logout();
      }
    }, 30000);

    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, onActivity, { passive: true }));
    return () => {
      window.clearInterval(timer);
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, onActivity));
    };
  }, [token]);

  const isSuperAdmin = !!user && isSuperAdminRole(user);
  const permissions = useMemo(() => {
    if (!user) return [];
    if (isSuperAdminRole(user)) return ['*'];
    if (Array.isArray(user.effectivePermissions)) return user.effectivePermissions;
    return Array.isArray(user.permissions) ? user.permissions : [];
  }, [user]);

  const can = (permission) => {
    if (!user || !permission) return false;
    if (permissions.includes('*')) return true;
    if (permissions.includes(permission)) return true;
    if (
      (permission === 'treks' || permission === 'treks_manage') &&
      (permissions.includes('treks') || permissions.includes('treks_manage'))
    ) {
      return true;
    }
    // Manage includes create for manual leads (create does not grant Lead Requests view).
    if (permission === 'lead_requests_create' && permissions.includes('lead_requests_manage')) {
      return true;
    }
    // If user has manage permission (e.g. registrations_manage), allow base permission (registrations).
    // Do not auto-grant create/export from manage via this path — those are separate checkboxes.
    if (
      !permission.endsWith('_manage') &&
      !permission.endsWith('_create') &&
      !permission.endsWith('_export')
    ) {
      const manageKey = `${permission}_manage`;
      if (permissions.includes(manageKey)) return true;
    }
    return false;
  };

  const value = {
    user,
    token,
    loading,
    login,
    sendOtp,
    loginWithOtp,
    logout,
    refreshUser,
    isAuthenticated: isAdminPanelUser(user),
    isSuperAdmin,
    permissions,
    can
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
